import { BodyM, Button, colorVariants, Heading2 } from '@casavo/base-ui'
import cn from 'classnames'
import useTranslation from 'next-translate/useTranslation'
import { useEffect, useState } from 'react'

import { useAuthentication } from '@/context/AuthProvider'
import { ContextValue } from '@/context/Cities'
import { useDevice } from '@/context/Device'
import { useModal } from '@/context/ModalContext'
import Bell from '@/design-lib/components/Icons/Bell'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { theme } from '@/design-lib/theme'
import { ListingCate } from '@/page-commons/listingBySlug'
import { flexCenter, gap, marginBottom, paddingBottom } from '@/shared-styles/styles.css'
import { Listings_Detail } from '@/types/cate-graphql-generated-types'
import { CasavoInventory } from '@/types/listing'
import { anchorSmoothScroll } from '@/utils/dom-interactions-helpers'
import { isStorageSupported } from '@/utils/isStorageSupported'
import { handleListingDetailClick } from '@/utils/tracking/eventsHandlers'

import Icon from '../Icon'
import SaveSearchDetail from '../Modal/Types/SaveSearch/Content/ListingContact/SaveSearchDetail'

type Props = {
  cities: ContextValue
  listing: ListingCate
  similarPropertiesLength: number
  userEmail: string
}

const SuccessSaveSearch: React.FC<Props> = ({ cities, listing, similarPropertiesLength, userEmail }) => {
  const { t } = useTranslation()
  const { openModal } = useModal()
  const device = useDevice()
  const { isLoading: isUserLoading, user } = useAuthentication()
  const [goToSimilarPropertiesClicked, setGoToSimilaPropertiesClicked] = useState(false)
  const { closeModal } = useModal()
  const [isSaveSearchSuccess, setSaveSearchSuccess] = useState<boolean>(false)
  const isIBuyerOrOdaInventory =
    listing.detail.inventory === CasavoInventory.iBuyer || listing.detail.inventory === CasavoInventory.Agency

  useEffect(() => {
    if (
      !isUserLoading &&
      user &&
      isStorageSupported(() => sessionStorage) &&
      sessionStorage.getItem('CASAVO@LISTINGS-SAVE-SEARCH-AFTER-LOGIN')
    ) {
      openSaveSearchModal()
    }
  }, [user, isUserLoading])

  const openSaveSearchModal = () =>
    openModal(
      <SaveSearchDetail
        cities={cities}
        listing={listing}
        similarPropertiesLength={similarPropertiesLength}
        userEmail={userEmail}
        onSaveSuccess={onSaveSuccess}
      />,
      {
        background: `linear-gradient(to bottom, ${theme.colors.white}, ${theme.colors.greyscale50})`,
        desktopHeight: 670,
        desktopWidth: 670,
        overlay: true,
      }
    )

  const onSaveSuccess = () => {
    setSaveSearchSuccess(true)
  }

  const handleOpenSaveSearchModal = (e: React.KeyboardEvent | React.MouseEvent) => {
    e.preventDefault()
    handleListingDetailClick('SaveSearchAccepted', isIBuyerOrOdaInventory ? 'Casavo' : 'Agency')
    openSaveSearchModal()
  }

  return (
    <>
      <Heading2 as="h2" className={paddingBottom['s-m']}>
        {t('contact-form:ContactForm_SuccessSaveSearch_Title')}
      </Heading2>
      <BodyM className={paddingBottom['2xl']}>{t('contact-form:ContactForm_SuccessSaveSearch_Subtitle')}</BodyM>

      {isSaveSearchSuccess && (
        <div className={cn(flexCenter, gap.s, colorVariants.success600, sprinkles({ justifyContent: 'center' }))}>
          <Icon.Check />
          <BodyM strong>{t('contact-form:ContactForm_SuccessSaveSearch_OkFeedback')}</BodyM>
        </div>
      )}

      <Button
        center
        className={cn(isSaveSearchSuccess && sprinkles({ visibility: 'hidden' }), marginBottom.l)}
        Icon={(props) => <Bell {...props} />}
        label={t('contact-form:ContactForm_SuccessSaveSearch_CTA_Primary')}
        minWidth={190}
        styleVariant="primary"
        onClick={handleOpenSaveSearchModal}
      />

      {similarPropertiesLength > 0 && !isSaveSearchSuccess && (
        <Button
          center
          className={goToSimilarPropertiesClicked && sprinkles({ visibility: 'hidden' })}
          label={t('contact-form:ContactForm_SuccessSaveSearch_CTA_Secondary')}
          minWidth={190}
          styleVariant="secondary"
          onClick={() => {
            handleListingDetailClick('SaveSearchRefused', isIBuyerOrOdaInventory ? 'Casavo' : 'Agency')
            device === 'desktop' && anchorSmoothScroll('#similarProperties', -100)
            device === 'mobile' && closeModal()
            setTimeout(() => {
              device === 'mobile' ? anchorSmoothScroll('#similarProperties', 0) : setGoToSimilaPropertiesClicked(true)
            }, 500)
          }}
        />
      )}
    </>
  )
}
export default SuccessSaveSearch
